import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  EMPTY,
  catchError,
  exhaustMap,
  filter,
  fromEvent,
  merge,
  startWith,
  throttleTime,
} from 'rxjs';
import { AuthService } from '../auth/auth';
import { StateStoreService } from '../state-store/state-store';
import { OrgManagementApi } from '../org-management-api/org-management-api';

/** Observed foreground use, not a presence timer. No input contents or
 * client-supplied identity/timestamps leave the browser. The server binds
 * activity to the caller's home membership, never a managed customer. */
@Injectable({ providedIn: 'root' })
export class MemberAccessService {
  private readonly document = inject(DOCUMENT);
  private readonly auth = inject(AuthService);
  private readonly store = inject(StateStoreService);
  private readonly api = inject(OrgManagementApi);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly started = signal(false);

  constructor() {
    effect(onCleanup => {
      if (!this.started() || !this.auth.isAuthenticated() || !this.store.isStateReady()) return;
      // Reading caller identity restarts (and cancels) tracking on account
      // changes, even if authentication stays true between the accounts.
      if (!this.auth.authState()?.user?.userId) return;
      this.zone.runOutsideAngular(() => {
        const subscription = merge(
          fromEvent(this.document, 'pointerdown'),
          fromEvent(this.document, 'keydown'),
          fromEvent(this.document, 'wheel', { passive: true }),
          fromEvent(this.document, 'visibilitychange'),
          this.router.events.pipe(filter(event => event instanceof NavigationEnd))
        )
          .pipe(
            startWith(null),
            filter(() => this.document.visibilityState === 'visible'),
            // Leading only: no delayed report can credit an idle/hidden tab.
            throttleTime(60_000),
            exhaustMap(() =>
              this.api.recordMemberAccess().pipe(
                catchError(() => {
                  console.warn(
                    '[MemberAccess] Activity could not be recorded; next interaction will retry.'
                  );
                  return EMPTY;
                })
              )
            )
          )
          .subscribe();
        onCleanup(() => subscription.unsubscribe());
      });
    });
  }

  start(): void {
    this.started.set(true);
  }
}
